import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDateTime } from "../../../lib/format";
import { useApi } from "../../customer/hooks";
import { useConfirm } from "../../../components/common/ConfirmDialog";
import { errMsg } from "../orders/QueueOrder";

const ROLES = ["executive", "accounting", "customer_service", "sales_rep", "warehouse", "driver", "employee"];
const inp = "h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-navy";
const err = (e, t) => errMsg(e, t);

const RolePicker = ({ value, onChange, testPrefix }) => {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap gap-1.5">
      {ROLES.map((r) => (
        <button key={r} type="button" onClick={() => onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r])} data-testid={`${testPrefix}-${r}`}
          className={`h-9 rounded-full px-3 text-xs font-semibold ${value.includes(r) ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{t(`ops.role.${r}`)}</button>
      ))}
    </div>
  );
};

const Invite = ({ reps, onLink }) => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [f, setF] = useState({ email: "", name: "", roles: ["employee"], sales_rep_code: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users/staff", f);
      onLink(data.invite_link);
      setF({ email: "", name: "", roles: ["employee"], sales_rep_code: "" });
      qc.invalidateQueries({ queryKey: ["staffUsers"] });
      qc.invalidateQueries({ queryKey: ["permLog"] });
    } catch (e2) { toast.error(err(e2, t)); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white p-4 shadow-sm" data-testid="staff-invite-form">
      <h2 className="text-base font-semibold text-navy md:text-lg">{t("ops.inviteStaff")}</h2>
      <div className="grid gap-2 sm:grid-cols-3">
        <input className={inp} placeholder="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="staff-invite-email" />
        <input className={inp} placeholder={t("users.name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-invite-name" />
        <select className={inp} value={f.sales_rep_code} onChange={(e) => setF({ ...f, sales_rep_code: e.target.value })} data-testid="staff-invite-rep">
          <option value="">{t("ops.salesRep")}: {t("ops.none")}</option>{reps.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <RolePicker value={f.roles} onChange={(roles) => setF({ ...f, roles })} testPrefix="staff-invite-role" />
      <button type="submit" className="h-11 rounded-full bg-brand px-5 text-sm font-semibold text-white" data-testid="staff-invite-submit">{t("users.invite")}</button>
    </form>
  );
};

const Row = ({ u, reps }) => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [roles, setRoles] = useState(u.roles);
  const save = async (body) => {
    try {
      await api.patch(`/admin/users/${u.id}/staff`, body);
      qc.invalidateQueries({ queryKey: ["staffUsers"] }); qc.invalidateQueries({ queryKey: ["permLog"] });
      toast.success(t("admin.saved"));
    } catch (e) { toast.error(err(e, t)); setRoles(u.roles); }
  };
  const toggleActive = async () => {
    if (await confirm({ title: `${t(u.active ? "ops.deactivate" : "ops.activate")}: ${u.name}?`, danger: u.active })) save({ active: !u.active });
  };
  const changed = roles.slice().sort().join() !== u.roles.slice().sort().join();
  return (
    <li className={`space-y-2 p-4 ${u.active ? "" : "opacity-60"}`} data-testid={`staff-row-${u.email}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1"><p className="font-semibold text-navy">{u.name}</p><p className="text-xs text-slate-500">{u.email} · {t(`users.status.${u.active ? u.status : "deactivated"}`)}</p></div>
        <select className={inp} value={u.sales_rep_code || ""} onChange={(e) => save({ sales_rep_code: e.target.value })} data-testid={`staff-rep-${u.email}`}>
          <option value="">{t("ops.salesRep")}: {t("ops.none")}</option>{reps.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="button" onClick={toggleActive} className="h-11 rounded-full border border-slate-200 px-4 text-xs font-semibold text-navy" data-testid={`staff-active-${u.email}`}>{t(u.active ? "ops.deactivate" : "ops.activate")}</button>
      </div>
      <RolePicker value={roles} onChange={setRoles} testPrefix={`staff-role-${u.email}`} />
      {changed && <button type="button" onClick={() => save({ roles })} className="h-10 rounded-full bg-navy px-4 text-xs font-semibold text-white" data-testid={`staff-save-roles-${u.email}`}>{t("admin.save")}</button>}
    </li>
  );
};

export const StaffUsers = ({ onLink }) => {
  const { t } = useI18n();
  const users = useApi("staffUsers", "/admin/users", { type: "staff" }, { staleTime: 0 });
  const reps = useApi("salesReps", "/admin/users/sales-reps");
  const log = useApi("permLog", "/admin/users/permission-log", undefined, { staleTime: 0 });
  return (
    <div className="space-y-5" data-testid="staff-users">
      <Invite reps={reps.data || []} onLink={onLink} />
      <ul className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm" data-testid="staff-users-list">{(users.data || []).map((u) => <Row key={`${u.id}-${u.roles.join()}`} u={u} reps={reps.data || []} />)}</ul>
      <section className="rounded-2xl bg-white p-4 shadow-sm" data-testid="permission-log">
        <h2 className="text-base font-semibold text-navy md:text-lg">{t("ops.permLog")}</h2>
        <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
          {(log.data || []).map((l) => <li key={`${l.at}-${l.user_email}`}><span className="font-semibold text-navy">{fmtDateTime(l.at)}</span> · {l.actor} → {l.user_email}: {Object.entries(l.after).map(([k, v]) => `${k}: ${JSON.stringify(l.before[k] ?? null)} → ${JSON.stringify(v)}`).join("; ")}</li>)}
        </ul>
      </section>
    </div>
  );
};
